/*
 * Rebuy tiered gift guard (v3)
 * Removes Rebuy tiered free gift lines when the cart subtotal is below that
 * gift's tier threshold, no matter where the cart was changed: Smart Cart
 * drawer, native /cart page (full reload), product page, or theme AJAX.
 *
 * v2: the checkout gate saves unsaved quantity changes BEFORE checking
 *     gifts, then goes straight to /checkout.
 * v3: on the /cart page the Checkout button is greyed out while the cart is
 *     updating and re-enabled once the gift check has finished.
 *
 * Theme asset only. Does not touch Rebuy's app embed or bundle files.
 * Config is read from window.RebuyGiftGuardConfig (set in theme.liquid).
 */
(function () {
  'use strict';

  var CONFIG = window.RebuyGiftGuardConfig || {};
  var TIERS = CONFIG.tiers || {};                       // { "<_tier uuid>": thresholdInCents }
  var DEFAULT_THRESHOLD = CONFIG.defaultThreshold || 25000;
  var DEBUG = !!CONFIG.debug;
  var FALLBACK_UNLOCK_MS = CONFIG.fallbackUnlockMs || 6000;
  var RELOAD_FLAG = 'rbgg_reloaded';
  var LOCK_CLASS = 'rbgg-locked';
  var CHECKOUT_SELECTOR = '[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
  var QUANTITY_SELECTOR = '[name^="updates"], quantity-selector, .quantity-selector, [data-quantity], a[href*="/cart/change"]';

  var queue = Promise.resolve(false);
  var gating = false;
  var reloading = false;
  var locked = false;
  var lockTimer = null;

  function log() {
    if (DEBUG) console.log.apply(console, ['[gift-guard]'].concat([].slice.call(arguments)));
  }

  function onCartPage() {
    return /^\/cart\/?$/.test(location.pathname);
  }

  function isCheckoutControl(el) {
    return !!el && !el.closest('#rebuy-cart, .rebuy-cart');
  }

  // ---- Gift check ---------------------------------------------------------

  function isRebuyGift(item) {
    var p = item.properties || {};
    return p._source === 'Rebuy' && !!p._tier && item.price === 0;
  }

  function thresholdFor(item) {
    return TIERS[item.properties._tier] || DEFAULT_THRESHOLD;
  }

  function staleGifts(cart) {
    var subtotal = cart.items_subtotal_price;
    return cart.items.filter(function (i) {
      return isRebuyGift(i) && subtotal < thresholdFor(i);
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (l) { updates[l.key] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function afterRemoval(cart, reason) {
    document.dispatchEvent(new CustomEvent('rebuy-gift-guard:removed', { detail: { cart: cart } }));
    try {
      if (window.Rebuy && Rebuy.Cart && typeof Rebuy.Cart.fetchCart === 'function') Rebuy.Cart.fetchCart();
    } catch (e) { log('rebuy resync failed', e); }
    // The native cart page is server rendered, so re-render it once
    // (skipped when checkout is about to navigate away anyway).
    // Never reload while a checkout click is being processed.
    if (!gating && reason !== 'checkout' && onCartPage() && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      reloading = true;
      location.reload();
    }
  }

  function runCheck(reason) {
    return fetchCart()
      .then(function (cart) {
        var stale = staleGifts(cart);
        log(reason, 'subtotal', cart.items_subtotal_price, 'stale gifts', stale.length);
        if (!stale.length) { sessionStorage.removeItem(RELOAD_FLAG); return false; }
        return removeLines(stale).then(function (newCart) { afterRemoval(newCart, reason); return true; });
      })
      .catch(function (e) { log('check failed', e); return false; });
  }

  // Checks run one after another, so a checkout click always waits for any
  // check already in progress and then runs its own fresh check.
  function check(reason) {
    queue = queue.then(function () { return runCheck(reason); });
    return queue;
  }

  // ---- Greyed-out Checkout button on the cart page ------------------------

  function injectStyle() {
    if (document.getElementById('rbgg-style')) return;
    var st = document.createElement('style');
    st.id = 'rbgg-style';
    st.textContent = '.' + LOCK_CLASS + '{opacity:.5!important;cursor:not-allowed!important;pointer-events:none!important;}';
    (document.head || document.documentElement).appendChild(st);
  }

  function applyLock() {
    [].slice.call(document.querySelectorAll(CHECKOUT_SELECTOR)).forEach(function (el) {
      if (!isCheckoutControl(el)) return;
      el.classList.toggle(LOCK_CLASS, locked);
      if (locked) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
      if (el.tagName === 'BUTTON') el.disabled = locked;
    });
  }

  function setLocked(value) {
    if (!onCartPage()) return;
    locked = value;
    applyLock();
    log(value ? 'checkout locked' : 'checkout unlocked');
  }

  // Run a check, then unlock unless the page is about to reload or a
  // checkout is already under way.
  function checkThenUnlock(reason) {
    return check(reason).then(function () {
      if (!reloading && !gating) {
        clearTimeout(lockTimer);
        setLocked(false);
      }
    });
  }

  function onQuantityActivity(e) {
    if (!onCartPage() || !e.target || !e.target.closest) return;
    if (!e.target.closest(QUANTITY_SELECTOR) || e.target.closest('#rebuy-cart, .rebuy-cart')) return;
    setLocked(true);
    clearTimeout(lockTimer);
    // Safety net: never leave the button stuck if the theme does not reload.
    lockTimer = setTimeout(function () { checkThenUnlock('fallback'); }, FALLBACK_UNLOCK_MS);
  }

  if (onCartPage()) {
    injectStyle();
    setLocked(true);
    // If the theme re-renders the cart without a reload, keep the new
    // Checkout button in the same locked or unlocked state.
    if (window.MutationObserver && document.body) {
      new MutationObserver(function () { if (locked) applyLock(); })
        .observe(document.body, { childList: true, subtree: true });
    }
    ['input', 'change', 'click'].forEach(function (type) {
      document.addEventListener(type, onQuantityActivity, true);
    });
  }

  // ---- Triggers -----------------------------------------------------------
  // Every page load, back/forward cache restores, returning to the tab,
  // Rebuy's cart events (which fire for non-Rebuy changes too) and the
  // theme's own cart events.

  checkThenUnlock('load');
  window.addEventListener('pageshow', function (e) { if (e.persisted) checkThenUnlock('pageshow'); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') check('visible');
  });
  ['rebuy:cart.ready', 'rebuy:cart.change', 'cart:change', 'cart:refresh'].forEach(function (name) {
    document.addEventListener(name, function () { checkThenUnlock(name); });
  });

  // ---- Pre-checkout gate --------------------------------------------------

  function findCartForm(el) {
    return el.form || el.closest('form') ||
      (onCartPage() ? document.querySelector('form[action$="/cart"]') : null);
  }

  // Save quantity boxes, note and attributes the customer changed but has not
  // submitted yet, so the gift check sees the real final cart.
  function savePendingChanges(form) {
    if (!form) return Promise.resolve();
    var data = new FormData(form);
    var hasChanges = false;
    data.forEach(function (v, k) {
      if (/^updates/.test(k) || k === 'note' || /^attributes/.test(k)) hasChanges = true;
    });
    if (!hasChanges) return Promise.resolve();
    log('saving pending cart changes before checkout');
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: data
    }).catch(function (e) { log('saving changes failed', e); });
  }

  function gate(el) {
    if (gating) return;
    gating = true;
    clearTimeout(lockTimer);
    if (el.tagName === 'BUTTON') el.disabled = true;
    var target = (el.href && /\/checkout/.test(el.href)) ? el.href : '/checkout';
    savePendingChanges(findCartForm(el))
      .then(function () { return check('checkout'); })
      .then(function () {
        // Go straight to checkout. Re-submitting the cart form here could
        // re-add the gift lines that were just removed.
        location.href = target;
      });
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!isCheckoutControl(el)) return;
    e.preventDefault();
    e.stopPropagation();
    if (locked) return;
    gate(el);
  }, true);

  // Covers pressing Enter in a cart field when Checkout is the form's button.
  document.addEventListener('submit', function (e) {
    var s = e.submitter;
    if (!s || s.name !== 'checkout' || !isCheckoutControl(s)) return;
    e.preventDefault();
    e.stopPropagation();
    if (locked) return;
    gate(s);
  }, true);

  window.RebuyGiftGuard = { check: check, gate: gate };
})();